import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withModifiers as _withModifiers, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-18957edf"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "product-page" }
const _hoisted_2 = { class: "product-page__image-cell" }
const _hoisted_3 = {
  key: 0,
  class: "product-page__image-preview"
}
const _hoisted_4 = ["src"]
const _hoisted_5 = { class: "product-page__image-overlay" }
const _hoisted_6 = {
  key: 1,
  class: "product-page__upload-control"
}
const _hoisted_7 = ["disabled", "data-product-id"]
const _hoisted_8 = { class: "product-page__image-cell" }
const _hoisted_9 = {
  key: 0,
  class: "product-page__image-preview"
}
const _hoisted_10 = ["src"]
const _hoisted_11 = { class: "product-page__image-overlay" }
const _hoisted_12 = {
  key: 1,
  class: "product-page__upload-control"
}
const _hoisted_13 = ["disabled", "data-product-id"]
const _hoisted_14 = { class: "product-page__row-actions" }

import { computed, onMounted, reactive, ref, shallowRef } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Delete, Edit, Plus, Upload } from '@element-plus/icons-vue'
import {
  CrudDialog,
  CrudForm,
  CrudSearch,
  CrudTable
} from '@/components/crud-v2'
import { usePagedCrud } from '@/hooks/crud/usePagedCrud'
import { usePermission } from '@/hooks/usePermission'
import {
  createProductFormItems,
  createProductSearchItems,
  createProductTableColumns
} from './product.config'
import type { ProductForm } from './product.config'
import {
  createProduct,
  deleteProduct,
  deleteProductImage,
  fetchLiveRoomOptions,
  fetchProductPage,
  updateProduct,
  updateProductHotSell,
  updateProductImage,
  updateProductVisible,
  uploadProductAsset
} from './product.service'
import type {
  ProductImageType,
  ProductPayload,
  ProductRecord,
  ProductStatus
} from './product.service'

type FormRef = InstanceType<typeof CrudForm>
type DialogMode = 'create' | 'edit'


export default /*#__PURE__*/_defineComponent({
  __name: 'Product',
  setup(__props) {

const canList = usePermission('product', 'list')
const canCreate = usePermission('product', 'create')
const canUpdate = usePermission('product', 'update')
const canDelete = usePermission('product', 'delete')

const {
  filters,
  pageNo,
  pageSize,
  records,
  total,
  error,
  loading,
  load,
  search,
  reset,
  changePage,
  changePageSize,
  refresh,
  refreshAfterDelete
} = usePagedCrud({
  createFilters: () => ({
    liveRoom: '',
    name: '',
    isShow: '' as ProductStatus | ''
  }),
  fetchPage: fetchProductPage
})

const errorMessage = computed(() =>
  error.value instanceof Error ? error.value.message : ''
)

const liveRooms = shallowRef<Array<{ unionId: string; eventName: string }>>([])
const liveRoomOptions = computed(() =>
  liveRooms.value.map((item) => ({
    label: item.eventName,
    value: item.unionId
  }))
)
const liveRoomNames = computed(
  () => new Map(liveRooms.value.map((item) => [item.unionId, item.eventName]))
)
const searchItems = computed(() =>
  createProductSearchItems(liveRoomOptions.value)
)
const formItems = computed(() => createProductFormItems(liveRoomOptions.value))
const tableColumns = createProductTableColumns(canUpdate || canDelete)

const dialogVisible = ref(false)
const dialogMode = ref<DialogMode>('create')
const formRef = ref<FormRef>()
const editingProduct = shallowRef<ProductRecord>()
const form = reactive<ProductForm>(createEmptyForm())

const dialogTitle = computed(() =>
  dialogMode.value === 'create' ? '新增商品' : '编辑商品'
)

const rules = {
  name: { required: true, message: '商品名称是必传的', trigger: 'blur' },
  buyLink: { required: true, message: '商品链接是必传的', trigger: 'blur' },
  liveRoom: {
    required: true,
    message: '直播间是必选的',
    trigger: 'change'
  }
}

function createEmptyForm(): ProductForm {
  return {
    name: '',
    monthPrice: '',
    twoMonthPrice: '',
    threeMonthPrice: '',
    fourMonthPrice: '',
    fiveMonthPrice: '',
    sixMonthPrice: '',
    yearPrice: '',
    deposit: '',
    buyLink: '',
    liveRoom: ''
  }
}

function openCreateDialog() {
  dialogMode.value = 'create'
  editingProduct.value = undefined
  Object.assign(form, createEmptyForm())
  dialogVisible.value = true
}

function openEditDialog(row: ProductRecord) {
  dialogMode.value = 'edit'
  editingProduct.value = row
  Object.keys(createEmptyForm()).forEach((key) => {
    const prop = key as keyof ProductForm
    form[prop] = row[prop]
  })
  dialogVisible.value = true
}

async function submitForm() {
  if (!(await formRef.value!.validate())) return false
  const payload: ProductPayload = { ...form }
  if (dialogMode.value === 'create') {
    await createProduct(payload)
  } else {
    await updateProduct(editingProduct.value!.id, payload)
  }
  return true
}

async function handleSubmitSuccess() {
  ElMessage.success(dialogMode.value === 'create' ? '新增成功' : '编辑成功')
  await refresh()
}

function showOperationError(cause: unknown, defaultMessage: string) {
  ElMessage.error(cause instanceof Error ? cause.message : defaultMessage)
}

const deletingId = ref<ProductRecord['id']>()

async function handleDelete(row: ProductRecord) {
  try {
    await ElMessageBox.confirm('确认删除此商品吗？', '提示', {
      confirmButtonText: '确认',
      cancelButtonText: '取消',
      type: 'warning'
    })
  } catch {
    return
  }

  deletingId.value = row.id
  try {
    await deleteProduct(row.id)
    ElMessage.success('删除成功')
    await refreshAfterDelete()
  } catch (cause) {
    showOperationError(cause, '删除失败')
  } finally {
    deletingId.value = undefined
  }
}

const changingStatuses = ref(new Set<string>())

function statusKey(row: ProductRecord, type: 'visible' | 'hotSell') {
  return `${row.id}:${type}`
}

function isStatusChanging(row: ProductRecord, type: 'visible' | 'hotSell') {
  return changingStatuses.value.has(statusKey(row, type))
}

function setStatusChanging(
  row: ProductRecord,
  type: 'visible' | 'hotSell',
  changing: boolean
) {
  const values = new Set(changingStatuses.value)
  const key = statusKey(row, type)
  if (changing) values.add(key)
  else values.delete(key)
  changingStatuses.value = values
}

async function confirmStatusChange(
  row: ProductRecord,
  type: 'visible' | 'hotSell'
) {
  const field = type === 'visible' ? 'isShow' : 'hotSell'
  const previousValue = row[field]
  const nextValue: ProductStatus = previousValue === '1' ? '0' : '1'
  const label = type === 'visible' ? '上架' : '热卖'

  try {
    await ElMessageBox.confirm(`是否调整当前商品“${label}”状态？`, '变更提醒', {
      confirmButtonText: '确认',
      cancelButtonText: '取消',
      type: 'warning'
    })
  } catch {
    row[field] = previousValue
    return false
  }

  setStatusChanging(row, type, true)
  row[field] = nextValue
  try {
    if (type === 'visible') await updateProductVisible(row.id, nextValue)
    else await updateProductHotSell(row.id, row.liveRoom, nextValue)
    ElMessage.success('状态更新成功')
    await refresh()
  } catch (cause) {
    row[field] = previousValue
    showOperationError(cause, '状态更新失败')
  } finally {
    setStatusChanging(row, type, false)
  }
  return false
}

const changingImages = ref(new Set<string>())

function imageKey(id: ProductRecord['id'], type: ProductImageType) {
  return `${id}:${type}`
}

function isImageChanging(row: ProductRecord, type: ProductImageType) {
  return changingImages.value.has(imageKey(row.id, type))
}

function setImageChanging(
  id: ProductRecord['id'],
  type: ProductImageType,
  changing: boolean
) {
  const values = new Set(changingImages.value)
  const key = imageKey(id, type)
  if (changing) values.add(key)
  else values.delete(key)
  changingImages.value = values
}

async function handleImageUpload(
  id: ProductRecord['id'],
  type: ProductImageType,
  file: File
) {
  setImageChanging(id, type, true)
  try {
    const imageUrl = await uploadProductAsset(file)
    await updateProductImage(id, type, imageUrl)
    ElMessage.success('图片上传成功')
    await refresh()
  } catch (cause) {
    showOperationError(cause, '图片上传失败')
  } finally {
    setImageChanging(id, type, false)
  }
}

function openImagePicker(event: MouseEvent) {
  const container = (event.currentTarget as HTMLElement).parentElement!
  container.querySelector<HTMLInputElement>('input[type="file"]')!.click()
}

async function handleImageFileChange(event: Event) {
  const input = event.currentTarget as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return

  const id = input.dataset.productId
  const type = input.dataset.imageType
  if (!id || (type !== 'cover' && type !== 'payQrCode')) {
    ElMessage.error('上传商品信息无效')
    input.value = ''
    return
  }

  await handleImageUpload(id, type, file)
  input.value = ''
}

async function handleDeleteImage(row: ProductRecord, type: ProductImageType) {
  const label = type === 'cover' ? '商品图片' : '支付二维码'
  try {
    await ElMessageBox.confirm(`确认删除${label}？`, '提示', {
      confirmButtonText: '确认',
      cancelButtonText: '取消',
      type: 'warning'
    })
  } catch {
    return
  }

  setImageChanging(row.id, type, true)
  try {
    await deleteProductImage(row.id, type)
    ElMessage.success('图片删除成功')
    await refresh()
  } catch (cause) {
    showOperationError(cause, '图片删除失败')
  } finally {
    setImageChanging(row.id, type, false)
  }
}

async function initialize() {
  try {
    liveRooms.value = await fetchLiveRoomOptions()
  } catch (cause) {
    showOperationError(cause, '直播间选项加载失败')
  }
  if (canList) await load()
}

onMounted(initialize)

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_switch = _resolveComponent("el-switch")!
  const _component_el_input = _resolveComponent("el-input")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(CrudSearch), {
      model: _unref(filters),
      items: searchItems.value,
      loading: _unref(loading),
      onSearch: _unref(search),
      onReset: _unref(reset)
    }, null, 8, ["model", "items", "loading", "onSearch", "onReset"]),
    _createVNode(_unref(CrudTable), {
      rows: _unref(records),
      "row-key": "id",
      "page-no": _unref(pageNo),
      "page-size": _unref(pageSize),
      total: _unref(total),
      loading: _unref(loading),
      "error-message": errorMessage.value,
      columns: _unref(tableColumns),
      onPageChange: _unref(changePage),
      onPageSizeChange: _unref(changePageSize),
      onRetry: _unref(refresh)
    }, {
      title: _withCtx(() => [
        _createTextVNode("直播间商品管理")
      ]),
      actions: _withCtx(() => [
        (_unref(canCreate))
          ? (_openBlock(), _createBlock(_component_el_button, {
              key: 0,
              type: "primary",
              onClick: openCreateDialog
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(Plus), {
                  class: "product-page__button-icon",
                  "aria-hidden": "true"
                }),
                _createTextVNode(" 新增 ")
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      liveRoom: _withCtx(({ row }) => [
        _createTextVNode(_toDisplayString(liveRoomNames.value.get(row.liveRoom)), 1)
      ]),
      cover: _withCtx(({ row }) => [
        _createElementVNode("div", _hoisted_2, [
          (row.cover)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createElementVNode("img", {
                  src: row.cover,
                  alt: "商品图片"
                }, null, 8, _hoisted_4),
                _createElementVNode("div", _hoisted_5, [
                  _createVNode(_component_el_button, {
                    type: "danger",
                    size: "small",
                    loading: isImageChanging(row, 'cover'),
                    onClick: _withModifiers(($event: any) => (handleDeleteImage(row, 'cover')), ["stop"])
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(" 删除 ")
                    ]),
                    _: 2
                  }, 1032, ["loading", "onClick"])
                ])
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_6, [
                _createVNode(_component_el_button, {
                  type: "success",
                  size: "small",
                  loading: isImageChanging(row, 'cover'),
                  disabled: isImageChanging(row, 'cover'),
                  onClick: openImagePicker
                }, {
                  default: _withCtx(() => [
                    (!isImageChanging(row, 'cover'))
                      ? (_openBlock(), _createBlock(_unref(Upload), {
                          key: 0,
                          class: "product-page__button-icon",
                          "aria-hidden": "true"
                        }))
                      : _createCommentVNode("", true),
                    _createTextVNode(" 上传图片 ")
                  ]),
                  _: 2
                }, 1032, ["loading", "disabled"]),
                _createElementVNode("input", {
                  type: "file",
                  accept: ".jpg,.jpeg,.png",
                  disabled: isImageChanging(row, 'cover'),
                  "data-product-id": row.id,
                  "data-image-type": "cover",
                  onChange: handleImageFileChange
                }, null, 40, _hoisted_7)
              ]))
        ])
      ]),
      payQrCode: _withCtx(({ row }) => [
        _createElementVNode("div", _hoisted_8, [
          (row.payQrCode)
            ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                _createElementVNode("img", {
                  src: row.payQrCode,
                  alt: "支付二维码"
                }, null, 8, _hoisted_10),
                _createElementVNode("div", _hoisted_11, [
                  _createVNode(_component_el_button, {
                    type: "danger",
                    size: "small",
                    loading: isImageChanging(row, 'payQrCode'),
                    onClick: _withModifiers(($event: any) => (handleDeleteImage(row, 'payQrCode')), ["stop"])
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(" 删除 ")
                    ]),
                    _: 2
                  }, 1032, ["loading", "onClick"])
                ])
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_12, [
                _createVNode(_component_el_button, {
                  type: "success",
                  size: "small",
                  loading: isImageChanging(row, 'payQrCode'),
                  disabled: isImageChanging(row, 'payQrCode'),
                  onClick: openImagePicker
                }, {
                  default: _withCtx(() => [
                    (!isImageChanging(row, 'payQrCode'))
                      ? (_openBlock(), _createBlock(_unref(Upload), {
                          key: 0,
                          class: "product-page__button-icon",
                          "aria-hidden": "true"
                        }))
                      : _createCommentVNode("", true),
                    _createTextVNode(" 上传图片 ")
                  ]),
                  _: 2
                }, 1032, ["loading", "disabled"]),
                _createElementVNode("input", {
                  type: "file",
                  accept: ".jpg,.jpeg,.png",
                  disabled: isImageChanging(row, 'payQrCode'),
                  "data-product-id": row.id,
                  "data-image-type": "payQrCode",
                  onChange: handleImageFileChange
                }, null, 40, _hoisted_13)
              ]))
        ])
      ]),
      isShow: _withCtx(({ row }) => [
        _createVNode(_component_el_switch, {
          "model-value": row.isShow,
          "active-value": "1",
          "inactive-value": "0",
          loading: isStatusChanging(row, 'visible'),
          disabled: isStatusChanging(row, 'visible'),
          "before-change": () => confirmStatusChange(row, 'visible')
        }, null, 8, ["model-value", "loading", "disabled", "before-change"])
      ]),
      hotSell: _withCtx(({ row }) => [
        _createVNode(_component_el_switch, {
          "model-value": row.hotSell,
          "active-value": "1",
          "inactive-value": "0",
          loading: isStatusChanging(row, 'hotSell'),
          disabled: isStatusChanging(row, 'hotSell'),
          "before-change": () => confirmStatusChange(row, 'hotSell')
        }, null, 8, ["model-value", "loading", "disabled", "before-change"])
      ]),
      rowActions: _withCtx(({ row }) => [
        _createElementVNode("div", _hoisted_14, [
          (_unref(canUpdate))
            ? (_openBlock(), _createBlock(_component_el_button, {
                key: 0,
                type: "text",
                onClick: ($event: any) => (openEditDialog(row))
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(Edit), {
                    class: "product-page__button-icon",
                    "aria-hidden": "true"
                  }),
                  _createTextVNode(" 编辑 ")
                ]),
                _: 2
              }, 1032, ["onClick"]))
            : _createCommentVNode("", true),
          (_unref(canDelete))
            ? (_openBlock(), _createBlock(_component_el_button, {
                key: 1,
                type: "text",
                loading: deletingId.value === row.id,
                disabled: deletingId.value !== undefined,
                onClick: ($event: any) => (handleDelete(row))
              }, {
                default: _withCtx(() => [
                  (deletingId.value !== row.id)
                    ? (_openBlock(), _createBlock(_unref(Delete), {
                        key: 0,
                        class: "product-page__button-icon",
                        "aria-hidden": "true"
                      }))
                    : _createCommentVNode("", true),
                  _createTextVNode(" 删除 ")
                ]),
                _: 2
              }, 1032, ["loading", "disabled", "onClick"]))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }, 8, ["rows", "page-no", "page-size", "total", "loading", "error-message", "columns", "onPageChange", "onPageSizeChange", "onRetry"]),
    _createVNode(_unref(CrudDialog), {
      modelValue: dialogVisible.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((dialogVisible).value = $event)),
      title: dialogTitle.value,
      width: "min(640px, calc(100vw - 32px))",
      submit: submitForm,
      onSuccess: handleSubmitSuccess,
      onSubmitError: _cache[1] || (_cache[1] = ($event: any) => (showOperationError($event, '操作失败')))
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(CrudForm), {
          ref_key: "formRef",
          ref: formRef,
          model: form,
          items: formItems.value,
          rules: rules
        }, {
          number: _withCtx(({ item, model }) => [
            _createVNode(_component_el_input, {
              modelValue: model[item.prop],
              "onUpdate:modelValue": ($event: any) => ((model[item.prop]) = $event),
              type: "number",
              placeholder: item.placeholder
            }, null, 8, ["modelValue", "onUpdate:modelValue", "placeholder"])
          ]),
          _: 1
        }, 8, ["model", "items"])
      ]),
      _: 1
    }, 8, ["modelValue", "title"])
  ]))
}
}

})